import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star'; // For benefits/quality care
import ShieldIcon from '@mui/icons-material/Shield'; // For protection/prevention
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly'; // For pediatric focus
import SpaIcon from '@mui/icons-material/Spa'; // For comfort / why choose us
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'; // For cleaning step
import BrushIcon from '@mui/icons-material/Brush'; // For application step
import HighlightIcon from '@mui/icons-material/Highlight'; // For curing light
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For "Is it right for your child?"
import EventNoteIcon from '@mui/icons-material/EventNote'; // For when to consider
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu'; // For diet/caring for sealants
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // For safety/quality
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const PediatricSealantsPacificaDental = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Effectively prevents cavities on the chewing surfaces of back teeth.',
    'Painless, quick, and non-invasive application – no drilling or shots!',
    'Provides durable protection that can last for several years.',
    'Saves money in the long run by avoiding fillings and more complex treatments.',
    'Helps establish positive dental experiences for your child.',
    'Acts as a barrier, keeping food particles and bacteria out of tiny grooves.'
  ];

  const sealantProcess = [
    {
      title: '1. Gentle Cleaning & Tooth Preparation',
      description: 'First, our friendly team at Pacifica Dental will thoroughly clean the surfaces of your child\'s teeth that will be sealed. The teeth are then dried, and a special cotton roll may be used to keep them dry.',
      icon: <CleaningServicesIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '2. Preparing the Tooth Surface for Bonding',
      description: 'A mild etching solution is gently applied to the chewing surfaces for a few seconds. This microscopically roughens the surface, helping the sealant bond strongly to the tooth.',
      icon: <SpaIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} /> // Represents gentle prep
    },
    {
      title: '3. Applying the Sealant Material',
      description: 'The tooth is rinsed and dried again. Then, the thin, liquid sealant material is carefully painted onto the grooves and pits of the tooth.',
      icon: <BrushIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '4. Hardening with a Special Light',
      description: 'A special curing light is shone on the sealant for a few seconds. This light quickly hardens the sealant, forming a tough, protective shield.',
      icon: <HighlightIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '5. Checking for Comfort and a Happy Smile!',
      description: 'Finally, we check your child\'s bite to ensure the sealant is comfortable. That\'s it – a simple way to protect their smile!',
      icon: <ChildFriendlyIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    }
  ];

  const whyMolarsProne = [
    'Back teeth (molars and premolars) have deep pits and fissures (grooves) on their chewing surfaces.',
    'These tiny grooves can easily trap food particles and bacteria.',
    'Toothbrush bristles often cannot reach all the way into these narrow spaces to clean them effectively.',
    'This makes molars particularly vulnerable to decay, especially in children who are still developing their brushing skills.'
  ];

  const isRightForChild = [
    'As soon as their permanent molars erupt (around age 6 for first molars, and age 12-13 for second molars).',
    'Children who have a history of cavities or are at higher risk for decay.',
    'Sometimes, primary (baby) teeth with very deep grooves may also benefit from sealants.',
    'Any child or teen who wants an extra layer of protection against cavities on their back teeth.',
    'It\'s a proactive step towards a lifetime of healthy smiles!'
  ];

  const caringForSealants = [
    'Continue regular brushing twice a day with fluoride toothpaste and daily flossing.',
    'Maintain a balanced diet and limit sugary snacks and drinks.',
    'Visit Pacifica Dental for regular dental check-ups and cleanings. We will check the condition of the sealants and reapply them if necessary.',
    'Avoid chewing on very hard objects like ice or hard candies, which could potentially damage sealants (and natural teeth!).'
  ];
  
  const faqs = [
    {
      question: 'What are dental sealants made of?',
      answer: 'Dental sealants are typically made of a safe, plastic (resin-based) material. At Pacifica Dental, we use high-quality, BPA-free sealants when available and appropriate for your child.'
    },
    {
      question: 'How long do dental sealants last?',
      answer: 'Sealants can protect teeth for several years, often 5 to 10 years. During your child\'s regular check-ups at Pacifica Dental, we will monitor the sealants and can reapply them if they become worn or chipped.'
    },
    {
      question: 'Is the sealant procedure painful for my child?',
      answer: 'Not at all! Applying dental sealants is a quick, easy, and completely painless procedure. There are no needles or drilling involved, making it a very positive experience for children.'
    },
    {
        question: 'Are dental sealants safe?',
        answer: 'Yes, dental sealants are widely recognized by dental professionals, including the American Dental Association, as a safe and effective way to prevent tooth decay in children.'
    },
    {
        question: 'Do teeth with sealants still need fluoride?',
        answer: 'Yes. Fluoride helps protect all surfaces of the teeth from decay, while sealants specifically protect the grooved chewing surfaces of back teeth. They work together for optimal cavity prevention.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pediatric Dental Sealants | Pacifica Dental | Cavity Prevention for Kids</title>
        <meta name="description" content="Protect your child's smile from cavities with painless dental sealants at Pacifica Dental. Quick, effective cavity prevention for kids. Call (408) 869-0998." />
        <meta name="keywords" content="pediatric dental sealants Pacifica Dental, dental sealants children, cavity prevention kids, children's dentist Pacifica Dental, (408) 869-0998, San Jose CA pediatric dentistry, Bay Area kids dentist" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #e3f2fd 0%, #f5f7fa 100%)', // Light, friendly blue gradient
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Protect Your Child's Smile with Dental Sealants at Pacifica Dental!
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              A Simple, Painless Way to Prevent Cavities in Children's Teeth
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              At Pacifica Dental, we believe in proactive pediatric dental care to ensure your child enjoys a lifetime of healthy smiles. Dental sealants are a cornerstone of this preventive approach – a quick, easy, and painless treatment that provides powerful protection against cavities on the chewing surfaces of your child's back teeth (molars and premolars).
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Why Dental Sealants are Great for Kids
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                           <ShieldIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Sealant Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    The Quick & Easy Sealant Application Process
                  </Typography>
                  {sealantProcess.map((step, index) => (
                    <Box key={index} sx={{ mb: index === sealantProcess.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                         {step.icon}
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Why are Molars Prone to Cavities? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Why Do Children's Back Teeth Need Extra Protection?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#f0f4c3' : '#33390e' }}> {/* Light green/yellow background */}
                <Grid container spacing={2}>
                    {whyMolarsProne.map((reason, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2, alignItems: 'flex-start'}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.dark', mt:0.5}}>
                                        <CheckCircleOutlineIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={reason} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
                 <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', textAlign: 'center', color: 'text.secondary' }}>
                    Sealants act like tiny raincoats, keeping cavity-causing culprits out of these vulnerable areas!
                  </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />
            
            {/* Is Your Child a Good Candidate? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                When Should Your Child Get Dental Sealants?
              </Typography>
              <Alert severity="info" icon={<HelpOutlineIcon />} sx={{borderRadius: 1.5, p:2, mb:3 }} elevation={2}>
                <AlertTitle sx={{fontWeight: 'bold'}}>Proactive Prevention is Key</AlertTitle>
                We recommend discussing sealants with our pediatric dental team at Pacifica Dental as soon as your child's permanent molars begin to appear. Early application provides the best protection.
              </Alert>
              <Grid container spacing={2}>
                {isRightForChild.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}> {/* Adjusted for potentially more items */}
                    <Paper elevation={1} sx={{p:1.5, display: 'flex', alignItems: 'center', height: '100%', borderRadius: 1.5}}>
                      <EventNoteIcon color="success" sx={{mr: 1.5, fontSize: '1.8rem'}} />
                      <Typography variant="body1" sx={{fontSize: '1rem'}}>{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Caring For Teeth with Sealants Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Caring for Your Child's Sealed Teeth
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}> {/* Light green background */}
                    <List>
                        {caringForSealants.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    {index % 2 === 0 ? <BrushIcon /> : <RestaurantMenuIcon />}
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                     <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', textAlign: 'center', color: 'text.secondary' }}>
                        Remember, sealants are part of a complete preventive strategy, not a replacement for good home care and regular dental visits!
                    </Typography>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Dental Sealant FAQs for Parents at Pacifica Dental
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Pacifica Dental Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Pacifica Dental for Your Child's Sealants?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Child-Friendly & Gentle Approach", text: "Our team at Pacifica Dental is experienced in making dental visits positive and stress-free for children of all ages.", icon: <ChildFriendlyIcon color="primary" /> },
                      { title: "Focus on Preventive Care", text: "We are passionate about preventing dental problems before they start, and sealants are a key part of our pediatric preventive program.", icon: <ShieldIcon color="primary" /> },
                      { title: "Experienced Pediatric Care", text: "We understand the unique dental needs of children and provide age-appropriate care in a welcoming environment.", icon: <StarIcon color="primary" /> },
                      { title: "Safe & Effective Materials", text: "We use high-quality, proven sealant materials to ensure the best protection for your child's teeth.", icon: <VerifiedUserIcon color="primary" /> } // Using VerifiedUser for safety/quality
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Give Your Child the Gift of a Cavity-Protected Smile!
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Contact Pacifica Dental today to learn more about dental sealants or to schedule an appointment for your child.
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Child's Appointment
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call Pacifica Dental at <Link to="tel:4088690998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> – we're happy to answer your questions!
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default PediatricSealantsPacificaDental;